import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { formatDate, formatDateTime } from '@/lib/format';
import type { InvoiceDetail } from '@/api/invoices';
import { invoiceSaleTypeLabel, resolveSaleType } from '@/pages/invoices/invoice-format';

/** Customer cell → link to the customer, "{id} - {company|name}" like legacy. */
function customerValue(detail: InvoiceDetail) {
  const c = detail.customer;
  if (!c) return undefined;
  const name = c.company_name || c.customer_name;
  return (
    <Link to={`/customers/${c.customer_id}`} style={{ color: 'var(--vn-primary-text)' }}>
      {[c.customer_id, name].filter(Boolean).join(' - ')}
    </Link>
  );
}

/** Single service link, or a plain "n service(s)" count for multi-service invoices. */
function serviceValue(detail: InvoiceDetail) {
  const svc = detail.service;
  const ids = detail.service_ids ?? [];
  if (ids.length > 1) return `${ids.length} services`;
  const sid = svc?.service_id ?? detail.service_id ?? ids[0];
  if (!sid) return svc?.service_name || undefined;
  const label = [sid, svc?.service_name].filter(Boolean).join(' - ');
  return (
    <Link to={`/services/${sid}`} style={{ color: 'var(--vn-primary-text)' }}>
      {label || `#${sid}`}
    </Link>
  );
}

/** "Yes · at {date} · by {id}" for the confirmed/invoicable audit facts. */
function auditValue(on: boolean | undefined, at?: string | null, by?: number | null) {
  const parts = [on ? 'Yes' : 'No'];
  if (at) parts.push(formatDate(at));
  if (by != null && by > 0) parts.push(`by #${by}`);
  return parts.join(' · ');
}

/**
 * Invoice facts card — customer/service/dates/payment/exporter/revenue over the
 * shared InfoGrid, mirroring the legacy `InvoiceInfoSection` cells. The status /
 * type pills live in the page header; here we surface the record facts. Handles
 * nulls gracefully (InfoGrid renders an em dash for empty values).
 */
export function InvoiceInfoCard({ detail }: { detail: InvoiceDetail }) {
  const items: InfoItem[] = [
    { label: 'Customer', value: customerValue(detail) },
    { label: 'Service', value: serviceValue(detail) },
    { label: 'Account Managers', value: detail.employee_names || detail.customer?.account_manager_names?.join(', ') },
    {
      label: 'Apply Range',
      value:
        detail.invoice_apply_startdate || detail.invoice_apply_enddate
          ? `${formatDate(detail.invoice_apply_startdate)} – ${formatDate(detail.invoice_apply_enddate)}`
          : undefined,
    },
    { label: 'Due Date', value: formatDate(detail.invoice_duedate) },
    { label: 'Payment At', value: formatDateTime(detail.invoice_payment_at) },
    { label: 'Payment Method', value: detail.invoice_payment_method },
    { label: 'Billing Scope', value: detail.invoice_billing_scope },
    { label: 'Sale Type', value: resolveSaleType(detail.invoice_sale_type) ? invoiceSaleTypeLabel(detail.invoice_sale_type) : undefined },
    {
      label: 'Currency',
      value:
        detail.invoice_exchange_rate && detail.invoice_exchange_rate !== 1
          ? `${detail.invoice_currency || 'VND'} @ ${detail.invoice_exchange_rate.toLocaleString('en-US')}`
          : detail.invoice_currency || 'VND',
    },
    { label: 'Exporter Code', value: detail.invoice_exporter_code },
    { label: 'Exporter Status', value: detail.invoice_exporter_status },
    { label: 'Revenue Confirmed', value: auditValue(detail.revenue_confirmed, detail.revenue_confirmed_at, detail.revenue_confirmed_by) },
    { label: 'Invoicable', value: auditValue(detail.invoicable, detail.invoicable_changed_at, detail.invoicable_changed_by) },
    { label: 'Created', value: formatDateTime(detail.created_at) },
  ];

  if (detail.invoice_note) {
    items.push({ label: 'Note', value: detail.invoice_note, full: true });
  }

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Invoice Information</CardTitle>
      </CardHeader>
      <CardBody>
        <InfoGrid items={items} />
      </CardBody>
    </Card>
  );
}
